import axios from "axios";
import { tGlobal } from "@/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  // Demo Preview System (D1) — when a demo session is active, every
  // request must carry the Demo Token instead of the admin's real
  // JWT. The frontend keeps both in localStorage under separate keys
  // so the admin can exit demo and seamlessly return to their real
  // session without re-login. The control endpoints themselves
  // (start/end session) explicitly opt out by setting
  // `config.useRealToken = true` so the admin's identity is used.
  const demoToken = localStorage.getItem("tsw_demo_token");
  if (demoToken && !config.useRealToken) {
    config.headers.Authorization = `Bearer ${demoToken}`;
    return config;
  }
  const token = localStorage.getItem("tsw_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;

    // Demo Preview self-healing (D1 Feb 2026 — refined Feb 2026 P0 fix):
    // ONLY purge the demo token + restore the admin session when the
    // error indicates the demo session itself is dead/invalid. Errors
    // like `demo_readonly_mode` (412) and `demo_path_blocked` (423) are
    // the EXPECTED behaviour of D1 — they fire on every disallowed
    // verb/path under a healthy demo session (e.g. dashboards that
    // POST a tracking event on mount, exports the user tried to load,
    // etc.) and must NOT kill the impersonation. Killing the session
    // on those was the root cause of "all demo profiles flash and
    // redirect back to /admin" reported by Roberto.
    const DEMO_SESSION_DEAD_CODES = new Set([
      "errors.demo_session_revoked",
      "errors.demo_session_expired",
      "errors.demo_token_malformed",
      "errors.demo_identity_not_found",
      "errors.demo_preview_disabled",
    ]);
    if (typeof detail === "string" && DEMO_SESSION_DEAD_CODES.has(detail)) {
      try {
        localStorage.removeItem("tsw_demo_token");
        localStorage.removeItem("tsw_demo_identity");
        localStorage.removeItem("tsw_demo_session");
        const stashedTok = localStorage.getItem("tsw_admin_token_stash");
        const stashedUsr = localStorage.getItem("tsw_admin_user_stash");
        if (stashedTok) {
          localStorage.setItem("tsw_token", stashedTok);
          localStorage.removeItem("tsw_admin_token_stash");
        }
        if (stashedUsr) {
          localStorage.setItem("tsw_user", stashedUsr);
          localStorage.removeItem("tsw_admin_user_stash");
        }
      } catch { /* localStorage may be unavailable */ }
      if (typeof window !== "undefined" && window.location) {
        window.location.assign("/admin");
      }
      return Promise.reject(err);
    }

    if (status === 401) {
      // token invalid — clear and redirect to login if not already
      const path = window.location.pathname;
      if (!path.startsWith("/login") && !path.startsWith("/register") && path !== "/" && !path.startsWith("/verify-email")) {
        localStorage.removeItem("tsw_token");
        localStorage.removeItem("tsw_user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

/**
 * Flatten any axios error into a plain string suitable for toast.error().
 *
 * Why: FastAPI / Pydantic 422 responses return `detail` as an *array of error
 * objects* (`[{loc, msg, type, ...}]`). Passing that array straight into JSX
 * (or a sonner toast) crashes React with:
 *   "Objects are not valid as a React child (found: object with keys
 *    {type, loc, msg, input, url})."
 *
 * This helper accepts the raw axios error and always returns a string — never
 * an object, never an array, never `undefined`. It is intentionally permissive
 * on shape so every existing call site can be migrated without behavioural
 * changes: a string `detail` keeps working as before; a Pydantic array gets
 * humanised into "field: msg; field: msg"; anything else falls back to
 * `axios.message` then to `fallback`.
 *
 * It also converts the bare axios-message strings ("Request failed with
 * status code 404", "Network Error") into something a user can act on. This
 * matters because a stale SPA cached in a tester's browser may be pointed at
 * a deprecated preview URL — the resulting raw 404 reads like a backend
 * bug to the user, when it is actually a "force-refresh the page" instruction.
 *
 * Pure data formatter — no side-effects, no auth/JWT/localStorage usage.
 */
export const extractErrorMessage = (err, fallback) => {
  // Phase A18-B3 + A18-B4 — localize default + status-aware fallbacks AND
  // backend error codes. When the backend returns a stable error code in
  // `detail` of the form `errors.<snake_case>` (introduced in A18-B4), we
  // translate it via the i18n bridge. Unknown codes fall back to the raw
  // code string (visible in logs so we surface untranslated cases). The
  // server-side detail may include trailing `:context` (e.g. an out-of-
  // scope profile list) — we strip that and translate the bare code, then
  // append the context after the colon for visibility.
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    const trimmed = detail.trim();
    if (/^errors\.[a-z0-9_]+(:.*)?$/i.test(trimmed)) {
      const [code, ...rest] = trimmed.split(":");
      const ctx = rest.join(":");
      const translated = tGlobal(code);
      // tGlobal returns the literal path on miss — surface unknown codes
      // by returning the raw detail in that case for easier debugging.
      if (translated && translated !== code) {
        return ctx ? `${translated} (${ctx})` : translated;
      }
      return trimmed;
    }
    return trimmed;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((d) => {
        if (typeof d === "string") return d;
        if (d && typeof d === "object") {
          const loc = Array.isArray(d.loc)
            ? d.loc.filter((p) => p !== "body").join(".")
            : "";
          const msg = d.msg || d.message || tGlobal("apiFallback.invalidValue");
          return loc ? `${loc}: ${msg}` : msg;
        }
        return String(d);
      })
      .join("; ");
  }
  if (detail && typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }

  // Status-aware fallbacks for the common cases users actually see.
  const status = err?.response?.status;
  if (status === 401) return tGlobal("apiFallback.unauthorized");
  if (status === 403) return tGlobal("apiFallback.forbidden");
  if (status === 404) return tGlobal("apiFallback.notFound");
  if (status === 409) return err?.response?.data?.detail || tGlobal("apiFallback.conflict");
  // Demo Preview / Locked — emitted by the demo read-only middleware
  // (backend/app/demo/middleware.py) for path-blocklisted endpoints.
  // BUGFIX (CA/EU/GL smoke, Feb-2026): without this clause the toast
  // showed the raw axios string "Request failed with status code 423".
  if (status === 423) return tGlobal("apiFallback.demoLocked");
  if (status === 412) return tGlobal("apiFallback.demoLocked");
  if (status === 429) return tGlobal("apiFallback.tooManyRequests");
  if (status >= 500) return tGlobal("apiFallback.serverError");

  if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
    return tGlobal("apiFallback.networkError");
  }

  if (typeof err?.message === "string") return err.message;
  return fallback ?? tGlobal("apiFallback.genericError");
};

export default api;
